import { useAppSelector } from 'hooks/reduxHooks'
import type { OptimizelyFeatureFlags } from 'constants/optimizely'
import {
  getOptimizelyFeatureFlagVariation,
  isAllowedToTriggerExperiment,
} from 'lib/optimizely/optimizelyUtils'

/**
 * Read an Optimizely feature flag (simple on/off toggle).
 *
 * Returns `true` if the flag is enabled for the current user, `false` otherwise.
 * The flag decision is made during SSR and available immediately.
 *
 * For A/B experiments with variations, use `useExperiment` instead.
 *
 * @param flagId  A member of the `OptimizelyFeatureFlags` enum.
 *
 * @example
 * ```ts
 * import { OptimizelyFeatureFlags } from 'constants/optimizely'
 *
 * const payAtProperty = useFeatureFlag(OptimizelyFeatureFlags.croPayAtProperty)
 * if (payAtProperty) { ... }
 * ```
 */
function useFeatureFlag(flagId: OptimizelyFeatureFlags): boolean {
  return useAppSelector((state) => {
    const canReadFlag = isAllowedToTriggerExperiment(true, state.utm.content, flagId)

    return canReadFlag && getOptimizelyFeatureFlagVariation(state, flagId)
  })
}

export default useFeatureFlag
